import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { fromIntegrationToSelectOption } from './utils';
import { IssuesHttpService } from 'src/app/services/httpServices/issues/issues-http.service';
import { IntegrationModel } from 'src/app/models/api';
import { issuesTableColumns } from 'src/app/models/tableColumns/issuesTableColumns.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { platformToAiServicesMap } from 'src/app/common/integrationToFilterUtils';
import { getAiServiceDisplayName, getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { AiService } from 'src/app/models/api/agentStep.model';
import { RSA_DEMO_AI_SERVICES } from 'src/app/models/selectOptions/rsaDemoAiServices';

export class IssuesFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public severityFilters: MultiSelectFilters;
  public statusFilters: MultiSelectFilters;
  public labelsFilters: MultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;
  public sourceFilters: MultiSelectFilters;
  public search: TextFilter;
  public firstSeenFromDate: DateFilter;
  public firstSeenToDate: DateFilter;
  public classification: string | undefined;

  public readonly defaultSortColumn: ITableColumn | undefined = issuesTableColumns.find(
    (column) => column.sortKey === 'severity'
  );
  public readonly sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: false } : undefined
  );

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _issuesHttpService: IssuesHttpService,
    private readonly _permissionsService: PermissionsService,
    private readonly _rsaDemoEnabled = false
  ) {
    this._initializeFilters();
    this._integrationsOnUpdate();
    this._aiServiceFiltersOnUpdate();

    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      search: this.search.getFilterValue(),
      severity: this.severityFilters.getSelectedFiltersValues(),
      issueStatus: this.statusFilters.getSelectedFiltersValues(),
      issueLabels: this.labelsFilters.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      aiService: this.aiServiceFilters.getSelectedFiltersValues(),
      source: this.sourceFilters.getSelectedFiltersValues(),
      firstSeenFromDate: this.firstSeenFromDate.getFilterValue(),
      firstSeenToDate: this.firstSeenToDate.getFilterValue(),
      classification: this.classification,
    };
  }

  public setClassification(value: string | undefined): void {
    if (this.classification === value) {
      return;
    }
    this.classification = value;
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.severityFilters.getSelectedOptions(),
      ...this.statusFilters.getSelectedOptions(),
      ...this.labelsFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.sourceFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.Search:
          this.search.update(value as string | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.FirstSeenFromDate:
          this.firstSeenFromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.FirstSeenToDate:
          this.firstSeenToDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        default:
          break;
      }
    } else {
      if (value instanceof Array) {
        if (value.length == 0) {
          needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          value.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          this._updateAllOptions();

          needUpdate =
            this.severityFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Severity] || []) || needUpdate;
          needUpdate =
            this.statusFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.IssueStatus] || []) || needUpdate;
          needUpdate =
            this.labelsFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.IssueLabels] || []) || needUpdate;
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
          needUpdate =
            this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AiService] || []) || needUpdate;
          needUpdate =
            this.sourceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.IssueSource] || []) || needUpdate;
        }
      }
    }
    if (needUpdate) {
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.Severity:
        return this.severityFilters.getOptions$();
      case FilterTypesEnum.IssueStatus:
        return this.statusFilters.getOptions$();
      case FilterTypesEnum.IssueLabels:
        return this.labelsFilters.getOptions$();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getOptions$();
      case FilterTypesEnum.IssueSource:
        return this.sourceFilters.getOptions$();

      default:
        throw new Error(`IssuesFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach((option) => {
      if (option.filterType) {
        if (option.filterType in optionsIdsByCategory) {
          optionsIdsByCategory[option.filterType].add(option.id);
        } else {
          optionsIdsByCategory[option.filterType] = new Set([option.id]);
        }
      }
    });

    if (optionsIdsByCategory[FilterTypesEnum.Severity]) {
      this.severityFilters.updateIfNeeded(
        this.severityFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Severity] &&
              !optionsIdsByCategory[FilterTypesEnum.Severity].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.IssueStatus]) {
      this.statusFilters.updateIfNeeded(
        this.statusFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.IssueStatus] &&
              !optionsIdsByCategory[FilterTypesEnum.IssueStatus].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.IssueLabels]) {
      this.labelsFilters.updateIfNeeded(
        this.labelsFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.IssueLabels] &&
              !optionsIdsByCategory[FilterTypesEnum.IssueLabels].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Integration] &&
              !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.AiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.AiService] &&
              !optionsIdsByCategory[FilterTypesEnum.AiService].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.IssueSource]) {
      this.sourceFilters.updateIfNeeded(
        this.sourceFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.IssueSource] &&
              !optionsIdsByCategory[FilterTypesEnum.IssueSource].has(selectedOption.id)
          )
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: {
        q: filtersQuery,
      },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.Severity:
        return this.severityFilters.getSelectedFiltersIds();
      case FilterTypesEnum.IssueStatus:
        return this.statusFilters.getSelectedFiltersIds();
      case FilterTypesEnum.IssueLabels:
        return this.labelsFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getSelectedFiltersIds();
      case FilterTypesEnum.IssueSource:
        return this.sourceFilters.getSelectedFiltersIds();

      default:
        throw new Error(`IssuesFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values: string[] = [];

    if ('search' in keyValueFilters || this.search.getFilterValue() !== undefined) {
      this.search.update(keyValueFilters['search'] ? keyValueFilters['search'][0] : undefined);
      needUpdate = true;
    }

    if ('firstSeenFromDate' in keyValueFilters || this.firstSeenFromDate.getFilterValue() !== undefined) {
      this.firstSeenFromDate.update(
        keyValueFilters['firstSeenFromDate'] && keyValueFilters['firstSeenFromDate'][0]
          ? new Date(keyValueFilters['firstSeenFromDate'][0])
          : undefined
      );
      needUpdate = true;
    }

    if ('firstSeenToDate' in keyValueFilters || this.firstSeenToDate.getFilterValue() !== undefined) {
      this.firstSeenToDate.update(
        keyValueFilters['firstSeenToDate'] && keyValueFilters['firstSeenToDate'][0]
          ? new Date(keyValueFilters['firstSeenToDate'][0])
          : undefined
      );
      needUpdate = true;
    }

    values = 'severity' in keyValueFilters ? keyValueFilters['severity'] : [];
    needUpdate = this.severityFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'issueStatus' in keyValueFilters ? keyValueFilters['issueStatus'] : [];
    needUpdate = this.statusFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'issueLabels' in keyValueFilters ? keyValueFilters['issueLabels'] : [];
    needUpdate = this.labelsFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'aiService' in keyValueFilters ? keyValueFilters['aiService'] : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'source' in keyValueFilters ? keyValueFilters['source'] : [];
    needUpdate = this.sourceFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: {
            q: filtersQuery,
          },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.severityFilters.updateIfNeeded([]) ||
      this.statusFilters.updateIfNeeded([]) ||
      this.labelsFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.sourceFilters.updateIfNeeded([])
    );
  }

  public getDynamicFilters(): string[] {
    return [];
  }

  private _initializeFilters() {
    this.search = new TextFilter();
    this.firstSeenFromDate = new DateFilter();
    this.firstSeenToDate = new DateFilter();

    // Initialize severity filters - excluding 'low' severity
    this.severityFilters = new MultiSelectFilters([
      {
        id: 'critical',
        value: 'critical',
        displayName: 'Critical',
        filterType: FilterTypesEnum.Severity,
        categoryName: 'Severity',
      },
      {
        id: 'high',
        value: 'high',
        displayName: 'High',
        filterType: FilterTypesEnum.Severity,
        categoryName: 'Severity',
      },
      {
        id: 'medium',
        value: 'medium',
        displayName: 'Medium',
        filterType: FilterTypesEnum.Severity,
        categoryName: 'Severity',
      },
    ]);

    this.statusFilters = new MultiSelectFilters(
      [
        {
          id: 'open',
          value: 'open',
          displayName: 'Open',
          filterType: FilterTypesEnum.IssueStatus,
          categoryName: 'Status',
        },
        {
          id: 'resolved',
          value: 'resolved',
          displayName: 'Resolved',
          filterType: FilterTypesEnum.IssueStatus,
          categoryName: 'Status',
        },
      ],
      ['open']
    );

    this.labelsFilters = new MultiSelectFilters([
      {
        id: 'runTime',
        value: 'runTime',
        displayName: 'Run Time',
        filterType: FilterTypesEnum.IssueLabels,
        categoryName: 'Labels',
      },
      {
        id: 'buildTime',
        value: 'buildTime',
        displayName: 'Build Time',
        filterType: FilterTypesEnum.IssueLabels,
        categoryName: 'Labels',
      },
    ]);
    this.integrationFilters = new MultiSelectFilters([]);
    // Initialize AI Service filter as empty - will be populated dynamically based on customer integrations
    this.aiServiceFilters = new MultiSelectFilters([]);
    this.sourceFilters = new MultiSelectFilters([
      {
        id: 'ruleBased',
        value: 'ruleBased',
        displayName: 'Graph',
        filterType: FilterTypesEnum.IssueSource,
        categoryName: 'Analysis Type',
      },
      {
        id: 'socAgent',
        value: 'socAgent',
        displayName: 'AI Generated',
        filterType: FilterTypesEnum.IssueSource,
        categoryName: 'Analysis Type',
      },
    ]);

    this._updateAllOptions();
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.severityFilters.getOptions$().value,
      ...this.statusFilters.getOptions$().value,
      ...this.labelsFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
      ...this.aiServiceFilters.getOptions$().value,
      ...this.sourceFilters.getOptions$().value,
    ]);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for issue viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.ISSUE_VIEWER
      );

      const integrationsOptionsMap: ISelectOption[] = filteredIntegrations.map((integration) => {
        return fromIntegrationToSelectOption(integration);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _aiServiceFiltersOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for issue viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.ISSUE_VIEWER
      );

      const issueSupportedServices = new Set<AiService>([
        AiService.chatGptEnterprise,
        AiService.copilotStudio,
        AiService.agentforce,
      ]);

      const activeAiServices = new Set<AiService>();
      for (const integration of filteredIntegrations) {
        const services = platformToAiServicesMap[integration.platform];
        if (services) {
          services.filter((s) => issueSupportedServices.has(s)).forEach((s) => activeAiServices.add(s));
        }
      }

      // Add RSA demo services directly — they aren't tied to any platform integration
      if (this._rsaDemoEnabled) {
        const rsaDemoServices = Array.from(RSA_DEMO_AI_SERVICES) as AiService[];
        rsaDemoServices.forEach((service) => activeAiServices.add(service));
      }

      // Create filter options only for active services
      const aiServiceOptions: ISelectOption[] = Array.from(activeAiServices)
        .map((service) => ({
          id: `${FilterTypesEnum.AiService}-${service}`,
          value: service,
          displayName: getAiServiceDisplayName(service),
          filterType: FilterTypesEnum.AiService,
          categoryName: FilterTypesEnum.AiService.toString(),
          iconPath: getAiServiceLogoPath(service),
          withoutBeautifyDisplayName: true,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      const shouldUpdateSelectedOptions = this.aiServiceFilters.updateOptions(aiServiceOptions);
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }
}
